import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useT } from "@/i18n/I18nContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import MovixLogo from "@/components/MovixLogo";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2, Lock, ShieldCheck, Eye, EyeOff, ArrowRight, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const BG = "https://images.unsplash.com/photo-1601929862217-f1bf94503333?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBjYXIlMjBkYXJrJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3Nzg3MTE0NjB8MA&ixlib=rb-4.1.0&q=85";

export default function AcceptInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { t } = useT();

  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/users/invite/${token}`);
        setInvite(r.data);
      } catch (e) {
        setError(formatApiError(e.response?.data?.detail) || "Convite inválido");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error(t("acceptInvite.passwordTooShort"));
      return;
    }
    if (password !== confirm) {
      toast.error(t("acceptInvite.passwordsDontMatch"));
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/users/accept-invite", { token, password });
      setDone(true);
      toast.success(t("acceptInvite.success"));
      setTimeout(() => navigate("/login"), 2000);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen w-full relative bg-black flex flex-col overflow-hidden" data-testid="accept-invite-page">
      <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${BG})` }} />
      <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/65 to-black/95" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-transparent to-black/40" />

      <header className="relative z-10 flex items-center justify-between px-6 lg:px-12 py-6">
        <MovixLogo variant="light" size="md" />
        <LanguageSwitcher dark />
      </header>

      <main className="relative z-10 flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md bg-white/[0.04] backdrop-blur-xl border border-white/10 p-8 sm:p-10">
          {loading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="w-8 h-8 text-[#F5A623] animate-spin" />
            </div>
          ) : error ? (
            <div className="text-center py-6">
              <AlertTriangle className="w-12 h-12 mx-auto text-red-400 mb-4" />
              <h1 className="text-2xl font-display text-white mb-2">{t("acceptInvite.invalidTitle")}</h1>
              <p className="text-sm text-white/60 mb-6">{error}</p>
              <Link to="/login" className="text-[#F5A623] text-sm font-mono hover:underline">
                {t("acceptInvite.backToLogin")}
              </Link>
            </div>
          ) : done ? (
            <div className="text-center py-6">
              <ShieldCheck className="w-12 h-12 mx-auto text-emerald-400 mb-4" />
              <h1 className="text-2xl font-display text-white mb-2">{t("acceptInvite.successTitle")}</h1>
              <p className="text-sm text-white/60">{t("acceptInvite.redirecting")}</p>
            </div>
          ) : (
            <>
              <div className="mb-6">
                <div className="inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.25em] text-[#F5A623] mb-3">
                  <span className="w-1.5 h-1.5 bg-[#F5A623] inline-block" />
                  {t("acceptInvite.welcomeBadge")}
                </div>
                <h1 className="text-3xl font-display tracking-tight text-white mb-2">
                  {t("acceptInvite.helloName", { name: invite.name })}
                </h1>
                <p className="text-sm text-white/60">
                  {t("acceptInvite.bodyIntro", {
                    role: invite.role === "admin" ? t("users.roleAdmin") : t("users.roleOperator"),
                  })}
                </p>
                <div className="mt-4 px-3 py-2 bg-black/40 border border-white/10 text-xs font-mono text-white/80">
                  {invite.email}
                </div>
              </div>

              <form onSubmit={submit} className="space-y-4">
                <Field label={t("acceptInvite.newPassword")} icon={<Lock className="w-4 h-4 text-white/40" />}>
                  <div className="relative">
                    <Input
                      type={show ? "text" : "password"}
                      required minLength={6} value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="rounded-none bg-black/40 border-white/20 text-white pr-10"
                      data-testid="accept-password-input"
                    />
                    <button type="button" onClick={() => setShow(!show)} className="absolute right-2 top-1/2 -translate-y-1/2 text-white/60 hover:text-white">
                      {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </Field>
                <Field label={t("acceptInvite.confirmPassword")} icon={<Lock className="w-4 h-4 text-white/40" />}>
                  <Input
                    type={show ? "text" : "password"}
                    required minLength={6} value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    className="rounded-none bg-black/40 border-white/20 text-white"
                    data-testid="accept-confirm-input"
                  />
                </Field>
                <Button
                  type="submit" disabled={submitting}
                  className="w-full rounded-none bg-[#F5A623] hover:bg-[#D97706] text-black font-semibold py-6"
                  data-testid="accept-submit-button"
                >
                  {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowRight className="w-4 h-4 mr-2" />}
                  {t("acceptInvite.activate")}
                </Button>
              </form>
            </>
          )}
        </div>
      </main>
    </div>
  );
}

const Field = ({ label, children, icon }) => (
  <div className="space-y-1.5">
    <label className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/70 flex items-center gap-1.5">
      {icon}
      {label}
    </label>
    {children}
  </div>
);
